import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Layout from '@/components/Layout';
import { Plus, Briefcase, MessageSquare, User, FileText, Star, TrendingUp, Eye, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfessionalDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [listingsRes, conversationsRes] = await Promise.all([
        axios.get(`${API}/listings`),
        axios.get(`${API}/conversations`)
      ]);
      
      // Show listings that are relevant for professionals (requests from companies)
      const relevantListings = listingsRes.data.filter(l => l.listing_type === 'request' && l.status === 'active');
      setListings(relevantListings);
      setConversations(conversationsRes.data);

      try {
        const profileRes = await axios.get(`${API}/profiles/professional/${user.id}`);
        setProfile(profileRes.data);
      } catch (error) {
        // Profile not created yet
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#344B71]"></div>
        </div>
      </Layout>
    );
  }

  const stats = [
    { label: 'Opportunità', value: listings.length, icon: <Briefcase className="w-6 h-6" />, color: 'from-[#344B71] to-[#344B71]' },
    { label: 'Conversazioni', value: conversations.length, icon: <MessageSquare className="w-6 h-6" />, color: 'from-[#E8A007] to-[#E09612]' },
    { label: 'Profilo', value: profile ? '100%' : '0%', icon: <User className="w-6 h-6" />, color: 'from-emerald-500 to-emerald-600' },
    { label: 'Risposte', value: conversations.filter(c => c.messages_count > 0).length, icon: <Star className="w-6 h-6" />, color: 'from-purple-500 to-purple-600' }
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8 mt-16">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-playfair font-bold text-[#344B71]">
                Benvenuto, {user?.name || 'Professionista'}! 👋
              </h1>
              <p className="text-slate-600 mt-1">Scopri nuove opportunità di lavoro e fai crescere la tua attività</p>
            </div>
            <Button 
              onClick={() => navigate('/listings')}
              className="mt-4 md:mt-0 bg-[#E8A007] hover:bg-[#E09612] text-white rounded-full px-6 shadow-lg hover:shadow-xl transition-all"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Esplora Annunci
            </Button>
          </div>
        </motion.div>

        {/* Profile Alert */}
        {!profile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="mb-8 p-6 border-l-4 border-l-[#E8A007] bg-gradient-to-r from-[#E8A007]/5 to-transparent">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-[#E8A007]/10 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-[#E8A007]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#344B71]">Completa il tuo Profilo Professionale</h3>
                    <p className="text-sm text-slate-600">Le aziende cercano professionisti con profili completi</p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/profile')}
                  className="bg-[#344B71] hover:bg-[#344B71] text-white rounded-full"
                >
                  Completa Ora
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          {stats.map((stat, index) => (
            <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center text-white`}>
                  {stat.icon}
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#344B71]">{stat.value}</p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </div>
              </div>
            </Card>
          ))}
        </motion.div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Latest Opportunities - Takes 2 columns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2"
          >
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[#344B71]">Ultime Opportunità</h2>
                <Button 
                  onClick={() => navigate('/listings')} 
                  size="sm" 
                  variant="outline"
                  className="border-[#344B71] text-[#344B71] hover:bg-[#344B71] hover:text-white rounded-full"
                >
                  Vedi tutti
                </Button>
              </div>
              
              {listings.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">Nessuna opportunità disponibile</p>
                  <Button 
                    onClick={() => navigate('/listings')}
                    className="bg-[#344B71] hover:bg-[#344B71] text-white rounded-full"
                  >
                    Esplora tutti gli annunci
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {listings.slice(0, 5).map((listing) => (
                    <div 
                      key={listing.id} 
                      className="p-4 border border-slate-100 rounded-xl hover:border-[#344B71]/30 hover:shadow-md transition-all cursor-pointer"
                      onClick={() => navigate(`/listings/${listing.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="font-semibold text-[#344B71] hover:text-[#344B71]">{listing.title}</h3>
                            {listing.priority === 'alta' && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full">Urgente</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 line-clamp-1">{listing.description}</p>
                          <div className="flex items-center space-x-4 mt-2">
                            <span className="text-xs bg-[#344B71]/10 text-[#344B71] px-2 py-1 rounded-full">{listing.category}</span>
                            <span className="text-xs text-slate-400">{listing.sector}</span>
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-300" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </motion.div>

          {/* Recent Conversations - Takes 1 column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[#344B71]">Messaggi Recenti</h2>
                <Button 
                  onClick={() => navigate('/messages')} 
                  size="sm"
                  variant="ghost"
                  className="text-[#344B71] hover:text-[#344B71]"
                >
                  Vedi tutti
                </Button>
              </div>
              
              {conversations.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">Nessun messaggio</p>
                  <p className="text-xs text-slate-400 mt-1">Rispondi agli annunci per iniziare</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {conversations.slice(0, 4).map((conv) => (
                    <div 
                      key={conv.id}
                      className="flex items-center space-x-3 p-3 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/messages/${conv.id}`)}
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        {conv.other_user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#344B71] text-sm truncate">{conv.other_user?.name || 'Utente'}</p>
                        <p className="text-xs text-slate-500 truncate">{conv.last_message || 'Nessun messaggio'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Quick Actions */}
            <Card className="p-6 mt-6">
              <h3 className="font-semibold text-[#344B71] mb-4">Azioni Rapide</h3>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/profile')}
                  variant="outline"
                  className="w-full justify-start border-slate-200 text-slate-600 hover:border-[#344B71] hover:text-[#344B71]"
                >
                  <User className="w-4 h-4 mr-3" />
                  Modifica Profilo
                </Button>
                <Button
                  onClick={() => navigate('/listings')}
                  variant="outline"
                  className="w-full justify-start border-slate-200 text-slate-600 hover:border-[#344B71] hover:text-[#344B71]"
                >
                  <Briefcase className="w-4 h-4 mr-3" />
                  Cerca Opportunità
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfessionalDashboard;
